import React, { useMemo, useState } from "react";
import { ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Scatter, Legend } from "recharts";
import useUsageTrends from "../../hooks/useUsageTrends";

export default function UsageTrends() {
  // Dropdown state
  const [period, setPeriod] = useState("week"); // week | month | year

  // Derive hook params based on selection
  const { range, interval, label } = useMemo(() => {
    const now = new Date();

    // Helpers
    const startOfWeek = (d) => {
      const tmp = new Date(d);
      const day = tmp.getDay(); // 0 = Sun ... 6 = Sat
      const diffToMonday = (day + 6) % 7; // 0 for Mon, 6 for Sun
      tmp.setDate(tmp.getDate() - diffToMonday);
      tmp.setHours(0, 0, 0, 0);
      return tmp;
    };
    const endOfWeek = (d) => {
      const s = startOfWeek(d);
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      e.setHours(23, 59, 59, 999);
      return e;
    };
    const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
    const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    const startOfYear = (d) => new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
    const endOfYear = (d) => new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);

    switch (period) {
      case "month":
        return {
          range: { start: startOfMonth(now).toISOString(), end: endOfMonth(now).toISOString() },
          interval: "day",
          label: "This Month"
        };
      case "year":
        return {
          range: { start: startOfYear(now).toISOString(), end: endOfYear(now).toISOString() },
          interval: "month",
          label: "This Year"
        };
      case "week":
      default:
        return {
          range: { start: startOfWeek(now).toISOString(), end: endOfWeek(now).toISOString() },
          interval: "day",
          label: "This Week"
        };
    }
  }, [period]);

  const { data, loading } = useUsageTrends({ start: range.start, end: range.end, interval, metric: "visits" });

  // Build exact buckets for selected period from raw series (if available)
  const chartData = useMemo(() => {
    // Prefer a raw time series; fall back to raw usageTrends if they include per-point dates
    const series =
      data?.raw?.data?.series ||
      data?.raw?.series ||
      data?.raw?.data?.usageTrends ||
      data?.raw?.usageTrends;
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const toValue = (p) => {
      const v = p?.value ?? p?.visits ?? p?.sessions ?? p?.questions ?? 0;
      return Number.isFinite(v) ? Number(v) : 0;
    };

    // If raw series exists, generate canonical buckets for exact range
    if (Array.isArray(series) && range?.start && range?.end) {
      const startDate = new Date(range.start);
      const endDate = new Date(range.end);

      const points = series
        .map(p => {
          const tsRaw = p.timestamp || p.date || p.day || p.datetime || p.time;
          const ts = tsRaw ? new Date(tsRaw) : null;
          return ts ? { ts, value: toValue(p) } : null;
        })
        .filter(Boolean)
        .filter(p => p.ts >= startDate && p.ts <= endDate);

      if (period === "week") {
        // Monday -> Sunday
        const buckets = Array.from({ length: 7 }, (_, i) => ({ label: dayNames[i], value: 0 }));
        points.forEach(p => {
          // Map JS day (0=Sun..6=Sat) to 0..6 (Mon..Sun)
          const jsDay = p.ts.getDay();
          const idx = (jsDay + 6) % 7;
          buckets[idx].value += p.value;
        });
        return buckets.map(b => {
          const content1 = Math.round(b.value * 0.6);
          const content2 = Math.round(b.value * 0.2);
          const content3 = Math.round((content1 + content2) / 3);
          return { month: b.label, content1, content2, content3 };
        });
      }

      if (period === "month") {
        // Days 1..end of current month
        const daysInMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
        const buckets = Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, value: 0 }));
        points.forEach(p => {
          const day = p.ts.getDate(); // 1..31
          if (day >= 1 && day <= daysInMonth) {
            buckets[day - 1].value += p.value;
          }
        });
        return buckets.map(b => {
          const content1 = Math.round(b.value * 0.6);
          const content2 = Math.round(b.value * 0.2);
          const content3 = Math.round((content1 + content2) / 3);
          // Label as day number to avoid showing previous month
          return { month: String(b.day), content1, content2, content3 };
        });
      }

      if (period === "year") {
        // Jan..Dec
        const buckets = Array.from({ length: 12 }, (_, i) => ({ monthIndex: i, value: 0 }));
        points.forEach(p => {
          const m = p.ts.getMonth(); // 0..11
          buckets[m].value += p.value;
        });
        return buckets.map(b => {
          const content1 = Math.round(b.value * 0.6);
          const content2 = Math.round(b.value * 0.2);
          const content3 = Math.round((content1 + content2) / 3);
          return { month: monthNames[b.monthIndex], content1, content2, content3 };
        });
      }
    }

    // Fallback to pre-mapped data as-is (prefer showing API data over zeros)
    let fallback = (data?.data || []).map((d) => ({
      ...d,
      content3: (d.content1 + d.content2) / 3,
    }));

    // If user selected "This Month" and API returned month buckets (Jan, Feb, ...),
    // filter to ONLY the current month so we don't display other months.
    if (period === "month" && fallback.length > 0) {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const now = new Date();
      const currentMonthLabel = monthNames[now.getMonth()];
      fallback = fallback.filter(x => String(x.month).toLowerCase() === currentMonthLabel.toLowerCase());
    }

    return fallback;
  }, [data, period, range?.start, range?.end]);

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 min-h-[240px] flex flex-col">
      <h3 className="font-bold text-[12px] text-gray-900 mb-2">Usage Trends</h3>
      <div className="rounded-lg border border-gray-100 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">CHART TITLE</span>
          <div className="relative">
            <details className="group">
              <summary className="list-none cursor-pointer text-[10px] text-gray-600 flex items-center gap-1 select-none">
                {label}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0L5.23 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </summary>
              <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded shadow z-10 w-28">
                <button onClick={() => setPeriod("week")} className="w-full text-left px-3 py-1.5 text-[10px] hover:bg-gray-50">This Week</button>
                <button onClick={() => setPeriod("month")} className="w-full text-left px-3 py-1.5 text-[10px] hover:bg-gray-50">This Month</button>
                <button onClick={() => setPeriod("year")} className="w-full text-left px-3 py-1.5 text-[10px] hover:bg-gray-50">This Year</button>
              </div>
            </details>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <ComposedChart data={chartData} margin={{ top: 6, bottom: 0, left: 0, right: 0 }} barCategoryGap={12} barGap={6}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false} />
            <YAxis domain={[-60, 60]} ticks={[-60, -20, 0, 20, 60]} tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Bar dataKey="content2" name="Content" fill="#A78BFA" radius={[4, 4, 0, 0]} />
            <Bar dataKey="content3" name="Content" fill="#F472B6" radius={[4, 4, 0, 0]} />
            <Scatter dataKey="content1" name="Content" fill="#60A5FA" shape="circle" />
            <Legend verticalAlign="bottom" height={24} wrapperStyle={{ fontSize: 10, color: '#6B7280' }} />
          </ComposedChart>
        </ResponsiveContainer>
        {loading && <div className="text-[10px] text-gray-500 mt-1">Loading...</div>}
      </div>
    </div>
  );
}
